
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';
import { Calendar, ArrowRight, ArrowLeft } from 'lucide-react';

const BlogCategory = () => {
    const { category } = useParams();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPosts = async () => {
            setLoading(true);
            try {
                // Fetch published posts in this category
                const q = query(
                    collection(db, 'posts'),
                    where('published', '==', true),
                    where('category', '==', category),
                    orderBy('createdAt', 'desc')
                );
                const querySnapshot = await getDocs(q);
                const postsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setPosts(postsData);
            } catch (error) {
                console.error("Error fetching posts: ", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, [category]);

    return (
        <div className="pt-20 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
            <SEO
                title={`${category} | Blog Category`}
                description={`Read our latest articles on ${category}.`}
            />
            <div className="max-w-7xl mx-auto py-12">
                <FadeIn>
                    <div className="mb-8">
                        <Link to="/blog" className="text-gray-500 hover:text-[#A2CB52] flex items-center mb-4 transition-colors">
                            <ArrowLeft size={16} className="mr-1" /> Back to All Posts
                        </Link>
                        <h1 className="text-3xl md:text-4xl font-bold text-[#20222D]">
                            Category: <span className="text-[#A2CB52]">{category}</span>
                        </h1>
                    </div>

                    {loading ? (
                        <div className="text-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A2CB52] mx-auto"></div>
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
                            <p className="text-gray-500 text-lg">No posts found in this category.</p>
                            <Link to="/blog" className="mt-4 inline-block text-[#A2CB52] font-bold hover:underline">
                                Browse all posts
                            </Link>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {posts.map((post) => (
                                <article key={post.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow flex flex-col h-full">
                                    {post.imageUrl ? (
                                        <img
                                            src={post.imageUrl}
                                            alt={post.title}
                                            className="h-48 w-full object-cover"
                                        />
                                    ) : (
                                        <div className="h-48 w-full bg-gray-200 flex items-center justify-center text-gray-400">
                                            No Image
                                        </div>
                                    )}
                                    <div className="p-6 flex-1 flex flex-col">
                                        <div className="flex items-center text-sm text-gray-500 mb-3 space-x-4">
                                            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-semibold uppercase tracking-wide">
                                                {post.category || 'Uncategorized'}
                                            </span>
                                            <span className="flex items-center">
                                                <Calendar size={14} className="mr-1" />
                                                {post.createdAt?.seconds ? new Date(post.createdAt.seconds * 1000).toLocaleDateString() : 'Date N/A'}
                                            </span>
                                        </div>
                                        <h2 className="text-xl font-bold text-[#20222D] mb-3 line-clamp-2">
                                            <Link to={`/blog/${post.slug || post.id}`} className="hover:text-[#A2CB52] transition-colors">
                                                {post.title}
                                            </Link>
                                        </h2>
                                        <p className="text-gray-600 mb-4 line-clamp-3 text-sm flex-1">
                                            {post.excerpt}
                                        </p>
                                        <Link
                                            to={`/blog/${post.slug || post.id}`}
                                            className="inline-flex items-center text-[#A2CB52] font-bold hover:underline mt-auto"
                                        >
                                            Read Article <ArrowRight size={16} className="ml-1" />
                                        </Link>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </FadeIn>
            </div>
        </div>
    );
};

export default BlogCategory;
